<template>
  <div class="composer">
    <div v-if="!conversation.closed_at" class="composer__toolbar">
      <div class="composer__modes" role="tablist" aria-label="Message type">
        <button
          v-for="option in MODES"
          :key="option.mode"
          type="button"
          role="tab"
          class="composer__mode"
          :class="[`is-${option.mode}`, { 'is-selected': mode === option.mode }]"
          :aria-selected="mode === option.mode"
          @click="mode = option.mode"
        >
          <Lock v-if="option.mode === 'note'" :size="11" /> {{ option.label }}
        </button>
      </div>

      <div class="composer__status">
        <span v-if="lastRefreshedAt">Last refreshed {{ formatClockTime(lastRefreshedAt) }}</span>
        <button
          type="button"
          class="composer__refresh"
          :class="{ 'is-refreshing': isRefreshing }"
          :disabled="isRefreshing"
          title="Refresh messages"
          aria-label="Refresh messages"
          @click="refresh"
        >
          <RefreshCw :size="13" />
        </button>
      </div>
    </div>

    <p v-if="conversation.closed_at" class="composer__closed">{{ formatClosedNotice(conversation.closed_at) }}</p>

    <template v-else>
      <ul v-if="pendingAttachments.length" class="composer__attachments">
        <li v-for="pending in pendingAttachments" :key="pending.key" class="composer__attachment">
          <i v-if="pending.status === 'uploading'" class="fas fa-spinner fa-spin" /><Paperclip v-else :size="12" />
          <span class="composer__attachment-name">{{ pending.fileName }}</span>
          <button
            type="button"
            class="composer__attachment-remove"
            :title="`Remove ${pending.fileName}`"
            @click="removeAttachment(pending.key)"
          >
            <X :size="12" />
          </button>
        </li>
      </ul>

      <div class="composer__box" :class="{ 'is-disabled': !canCompose, 'is-note': isNote }">
        <button
          type="button"
          class="composer__attach"
          :disabled="!canCompose || pendingAttachments.length >= MAX_ATTACHMENTS_PER_MESSAGE"
          title="Attach a file"
          aria-label="Attach a file"
          @click="fileInput?.click()"
        >
          <Paperclip :size="16" />
        </button>
        <input
          ref="fileInput"
          type="file"
          multiple
          :accept="ATTACHMENT_ACCEPT"
          class="tw-hidden"
          @change="onFilesChosen"
        />
        <el-input
          v-model="content"
          type="textarea"
          :autosize="{ minRows: 1, maxRows: 6 }"
          :maxlength="MAX_CONTENT_CHARS"
          :disabled="!canCompose"
          :placeholder="isNote ? 'Write a note visible only to your team' : 'Type your reply'"
          resize="none"
          class="composer__input"
          @keydown.enter.meta.prevent="send"
          @keydown.enter.ctrl.prevent="send"
        />
        <el-button
          :type="isNote ? 'warning' : 'primary'"
          circle
          size="small"
          class="composer__send"
          :disabled="!canSend"
          :loading="isPending"
          :title="isNote ? 'Post note' : 'Send'"
          :aria-label="isNote ? 'Post note' : 'Send'"
          @click="send"
        >
          <template #icon><SendHorizontal :size="14" /></template>
        </el-button>
      </div>

      <p v-if="blocker" class="composer__hint">
        <Lock :size="12" /> {{ blocker.text }}
        <el-button
          v-if="blocker.action"
          link
          type="primary"
          size="small"
          class="composer__hint-action"
          :loading="isUnblocking"
          @click="blocker.action.run"
        >
          {{ blocker.action.label }}
        </el-button>
      </p>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue"
import { ElMessage } from "element-plus"
import { Lock, Paperclip, RefreshCw, SendHorizontal, X } from "lucide-vue-next"
import {
  PatientConversationDTO,
  PatientConversationMessageVisibilityEnum,
  PatientConversationStatusEnum
} from "@vitable/asclepius-api-client-ts"
import { useCurrentStaffId } from "@/modules/patient-conversations/composables/current-staff"
import {
  useChangePatientConversationAssignment,
  useChangePatientConversationStatus,
  useRefreshPatientConversation,
  useSendPatientConversationMessage,
  useUploadPatientConversationAttachment
} from "@/modules/patient-conversations/composables/patient-conversations"
import {
  ATTACHMENT_ACCEPT,
  describeRejectedFile,
  MAX_ATTACHMENTS_PER_MESSAGE
} from "@/modules/patient-conversations/constants/patient-conversation-attachments"
import { describePatientConversationError } from "@/modules/patient-conversations/constants/patient-conversation-error-codes"
import { formatClockTime, formatClosedNotice } from "@/modules/patient-conversations/utils/patient-conversation-format"

// TDP §5.2.3
const MAX_CONTENT_CHARS = 4000

type ComposerMode = "reply" | "note"

const MODES: ReadonlyArray<{ mode: ComposerMode; label: string }> = [
  { mode: "reply", label: "Reply to patient" },
  { mode: "note", label: "Internal note" }
]

interface PendingAttachment {
  key: number
  fileName: string
  status: "uploading" | "ready"
  /** `pcva_…` once uploaded */
  id?: string
}

interface ComposerBlocker {
  text: string
  action?: { label: string; run: () => Promise<void> }
}

const props = defineProps<{
  conversation: PatientConversationDTO
}>()

const emit = defineEmits<{
  (e: "sent"): void
}>()

const mode = ref<ComposerMode>("reply")
const isNote = computed<boolean>(() => mode.value === "note")
const content = ref<string>("")
const fileInput = ref<HTMLInputElement>()
const pendingAttachments = ref<Array<PendingAttachment>>([])
let nextAttachmentKey = 0

const { mutateAsync: sendMessage, isPending } = useSendPatientConversationMessage()
const { mutateAsync: uploadAttachment } = useUploadPatientConversationAttachment()
const { mutateAsync: changeAssignment, isPending: isAssigning } = useChangePatientConversationAssignment()
const { mutateAsync: changeStatus, isPending: isChangingStatus } = useChangePatientConversationStatus()
const { lastRefreshedAt, isRefreshing, refresh } = useRefreshPatientConversation(computed(() => props.conversation.id))

// Only the assignee of an Open conversation may message the patient (the server answers 409 otherwise);
// any allowed staff can post an internal note until the conversation is closed (TDP §5.1.2)
const currentStaffId = useCurrentStaffId()
const isAssignee = computed<boolean>(
  () => !!currentStaffId.value && props.conversation.assignee?.id === currentStaffId.value
)
const isOpen = computed<boolean>(() => props.conversation.status === PatientConversationStatusEnum.Open)
const canCompose = computed<boolean>(() => isNote.value || (isOpen.value && isAssignee.value))
const isUnblocking = computed<boolean>(() => isAssigning.value || isChangingStatus.value)

const blocker = computed<ComposerBlocker | undefined>(() => {
  if (isNote.value) return undefined
  if (props.conversation.status === PatientConversationStatusEnum.Archived)
    return { text: "This conversation is archived.", action: { label: "Unarchive", run: unarchive } }
  if (!isAssignee.value)
    return { text: "You must be assigned to message the patient.", action: { label: "Assign to me", run: assignToMe } }
  return undefined
})

async function assignToMe(): Promise<void> {
  if (!currentStaffId.value) return
  try {
    await changeAssignment({
      patientConversationId: props.conversation.id,
      request: { assignee_id: currentStaffId.value }
    })
  } catch (error) {
    ElMessage.error(describePatientConversationError(error))
  }
}

async function unarchive(): Promise<void> {
  try {
    await changeStatus({
      patientConversationId: props.conversation.id,
      request: { to_status: PatientConversationStatusEnum.Open }
    })
  } catch (error) {
    ElMessage.error(describePatientConversationError(error))
  }
}

const isUploading = computed<boolean>(() => pendingAttachments.value.some((pending) => pending.status === "uploading"))
const canSend = computed<boolean>(
  () => canCompose.value && content.value.trim().length > 0 && !isUploading.value && !isPending.value
)

function onFilesChosen(event: Event): void {
  const input = event.target as HTMLInputElement
  const files = Array.from(input.files ?? [])
  input.value = ""

  for (const file of files) {
    if (pendingAttachments.value.length >= MAX_ATTACHMENTS_PER_MESSAGE) {
      ElMessage.warning(`Up to ${MAX_ATTACHMENTS_PER_MESSAGE} attachments per message`)
      break
    }
    const rejection = describeRejectedFile(file)
    if (rejection) {
      ElMessage.error(rejection)
      continue
    }
    upload(file)
  }
}

async function upload(file: File): Promise<void> {
  const pending: PendingAttachment = { key: nextAttachmentKey++, fileName: file.name, status: "uploading" }
  pendingAttachments.value = [...pendingAttachments.value, pending]
  try {
    const attachment = await uploadAttachment({ patientConversationId: props.conversation.id, file })
    pendingAttachments.value = pendingAttachments.value.map((candidate) =>
      candidate.key === pending.key ? { ...candidate, status: "ready", id: attachment.id } : candidate
    )
  } catch (error) {
    removeAttachment(pending.key)
    ElMessage.error(`${file.name}: ${describePatientConversationError(error)}`)
  }
}

// Unsent uploads are purged server-side after 24 hours, so dropping one here is enough
function removeAttachment(key: number): void {
  pendingAttachments.value = pendingAttachments.value.filter((pending) => pending.key !== key)
}

async function send(): Promise<void> {
  if (!canSend.value) return
  const attachmentIds = pendingAttachments.value.flatMap((pending) => (pending.id ? [pending.id] : []))
  try {
    await sendMessage({
      patientConversationId: props.conversation.id,
      request: {
        content: content.value.trim(),
        visibility: isNote.value
          ? PatientConversationMessageVisibilityEnum.Internal
          : PatientConversationMessageVisibilityEnum.All,
        attachment_ids: attachmentIds.length ? attachmentIds : undefined
      }
    })
    content.value = ""
    pendingAttachments.value = []
    emit("sent")
  } catch (error) {
    ElMessage.error(describePatientConversationError(error))
  }
}
</script>

<style scoped lang="sass">
@import "@/styles/colors"

.composer
  padding: 8px 12px 12px
  border-top: 1px solid $border
  background-color: white

  &__toolbar
    display: flex
    align-items: center
    justify-content: space-between
    gap: 8px
    margin-bottom: 6px

  &__modes
    display: inline-flex
    gap: 2px
    padding: 2px
    border-radius: 8px
    background-color: $grey-light

  &__mode
    display: inline-flex
    align-items: center
    gap: 4px
    padding: 3px 10px
    border: 0
    border-radius: 6px
    background: transparent
    font-size: 12px
    font-weight: 600
    color: $grey
    cursor: pointer
    transition: background-color 0.15s, color 0.15s

    &:hover
      color: $empty-text

    &.is-selected
      background-color: white
      color: $primary
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08)

    &.is-note.is-selected
      color: $yellow

  &__status
    display: flex
    align-items: center
    justify-content: flex-end
    gap: 6px
    min-height: 20px
    font-size: 11px
    color: $grey

  &__refresh
    display: inline-flex
    align-items: center
    justify-content: center
    width: 20px
    height: 20px
    border: 0
    border-radius: 50%
    background: transparent
    color: $grey
    cursor: pointer

    &:hover:not(:disabled)
      background-color: $grey-light
      color: $primary

    &.is-refreshing svg
      animation: composer-spin 1s linear infinite

  &__attachments
    display: flex
    flex-wrap: wrap
    gap: 6px
    margin: 0 0 6px
    padding: 0
    list-style: none

  &__attachment
    display: inline-flex
    align-items: center
    gap: 5px
    max-width: 100%
    padding: 3px 4px 3px 8px
    border: 1px solid $border
    border-radius: 6px
    font-size: 12px
    color: $empty-text

  &__attachment-name
    overflow: hidden
    text-overflow: ellipsis
    white-space: nowrap

  &__attachment-remove
    display: inline-flex
    align-items: center
    justify-content: center
    width: 18px
    height: 18px
    border: 0
    border-radius: 50%
    background: transparent
    color: $grey
    cursor: pointer

    &:hover
      background-color: $grey-light
      color: $element-danger

  &__box
    display: flex
    align-items: flex-end
    gap: 4px
    padding: 4px 6px 4px 4px
    border: 1px solid $border
    border-radius: 12px
    background-color: $grey-light
    transition: border-color 0.15s, background-color 0.15s, box-shadow 0.15s

    &:focus-within
      border-color: $primary
      background-color: white
      box-shadow: 0 0 0 3px $highlight-light

    &.is-disabled
      border-style: dashed
      background-color: white

    // Internal notes: the amber of the notes in the thread, so the mode is unmistakable while typing
    &.is-note
      border-color: $yellow
      background-color: $yellow-background

      &:focus-within
        border-color: $yellow
        background-color: $yellow-background
        box-shadow: 0 0 0 3px rgba(229, 166, 76, 0.2)

  &__attach
    display: inline-flex
    align-items: center
    justify-content: center
    flex: none
    width: 28px
    height: 28px
    margin-bottom: 3px
    border: 0
    border-radius: 50%
    background: transparent
    color: $grey
    cursor: pointer

    &:hover:not(:disabled)
      background-color: white
      color: $primary

    &:disabled
      color: $border
      cursor: default

  &__input
    flex: 1 1 auto

    :deep(.el-textarea__inner)
      padding: 6px 8px
      background: transparent
      box-shadow: none
      font-size: 14px
      line-height: 1.5
      color: $empty-text

    :deep(.is-disabled .el-textarea__inner)
      cursor: not-allowed

  // Apollo's theme sets el-button height to auto; pin a square so the circle stays round
  &__send.el-button
    flex: none
    width: 28px
    height: 28px
    padding: 0
    margin-bottom: 3px

  &__hint
    display: flex
    align-items: center
    gap: 5px
    margin: 6px 2px 0
    font-size: 12px
    color: $grey

  &__hint-action.el-button
    height: auto
    padding: 0
    font-size: 12px

  &__closed
    margin: 0
    padding: 8px 0
    text-align: center
    font-size: 13px
    color: $grey

@keyframes composer-spin
  to
    transform: rotate(360deg)
</style>
